import React, { useState, useMemo, useRef, useEffect } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { useGetOldRequestNumberDropdownQuery } from '../../../../api/endpoints/teamProductivityMeasurement';
import { useLanguage } from '../../../../contexts/LanguageContext';

const OldRequestNumberMultiSelect = ({ value, onChange, className = '', disabled = false, excludeRequestNumber = null }) => {
    const { th } = useLanguage();
    const [searchText, setSearchText] = useState('');
    const [filterValue, setFilterValue] = useState('');
    const debounceTimerRef = useRef(null);

    const { data: list = [], isLoading } = useGetOldRequestNumberDropdownQuery(
    { rowCount: 100, searchText: searchText || undefined },
    { refetchOnMountOrArgChange: true }
   );

    const selectedValues = useMemo(() => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        return [value];
    }, [value]);

    const options = useMemo(() => {
        const apiOptions = list
            .filter((item) => item.requestNumber !== excludeRequestNumber)
            .map((item) => ({
                label: String(item.requestNumber),
                value: String(item.requestNumber),
                detail: item.requestDetail || ''
            }));

        const selectedOptions = selectedValues
            .filter((v) => !apiOptions.some((opt) => opt.value === String(v)))
            .map((v) => ({
                label: String(v),
                value: String(v),
                detail: ''
            }));

        return [...selectedOptions, ...apiOptions];
    }, [list, selectedValues, excludeRequestNumber]);

    const handleFilterInputChange = (inputValue) => {
        setFilterValue(inputValue);
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            setSearchText(inputValue);
        }, 300);
    };

    const filterTemplate = () => (
        <div className="p-multiselect-filter-container">
            <InputText value={filterValue} onChange={(e) => handleFilterInputChange(e.target.value)} className="p-multiselect-filter w-full" placeholder={th('common.search')} />
            <span className="p-multiselect-filter-icon pi pi-search" />
        </div>
    );

    const itemTemplate = (option) => {
        if (!option) return null;
        return (
            <div className="flex flex-column">
                <span className="font-medium">{option.label}</span>
                {option.detail && <small className="text-500">{option.detail}</small>}
            </div>
        );
    };

    const handleShow = () => {
        setFilterValue('');
        setSearchText('');
    };

    const handleHide = () => {
        setFilterValue('');
        setSearchText('');
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    return (
        <MultiSelect
            value={selectedValues}
            onChange={(e) => onChange(e.value || [])}
            onShow={handleShow}
            onHide={handleHide}
            options={options}
            optionLabel="label"
            optionValue="value"
            className={className}
            placeholder={th('teamProductivity.placeholders.oldRequestNumbers')}
            loading={isLoading}
            disabled={disabled}
            filter
            filterTemplate={filterTemplate}
            showClear
            display="chip"
            itemTemplate={itemTemplate}
            selectedItemsLabel={`{0} ${th('common.selected')}`}
            emptyFilterMessage={th('filter.noResultsFound')}
            showSelectAll={false}
        />
    );
};

export default OldRequestNumberMultiSelect;
